import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSuperMerchants } from "../../../redux/actions/profile";
import MerchantDetailPanel from "../../shared/MerchantDetailPanel";
import MerchantFilterBar from "../../shared/MerchantFilterBar";
import {
  DEFAULT_MERCHANT_FILTERS,
  applyMerchantFilters,
} from "../../../utility/merchantFilters";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";
const PAGE_SIZE = 20;

export default function AllMerchants() {
  const dispatch = useDispatch();
  const merchants = useSelector((s) => s.profile.superMerchants || []);
  const [filters, setFilters] = useState(DEFAULT_MERCHANT_FILTERS);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => { dispatch(getSuperMerchants()); }, [dispatch]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [filters]);

  const filtered = useMemo(
    () => applyMerchantFilters(merchants, filters),
    [merchants, filters]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const counts = {
    all: merchants.length,
    wallet: merchants.filter((m) => !!m.wallet).length,
    none: merchants.filter((m) => !m.wallet).length,
  };

  const isActive = (m) => m.subscriptionEnd && Date.now() < m.subscriptionEnd;

  const goTo = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>All Merchants</h2>
          <p style={S.sub}>Platform-wide merchant roster with wallet information</p>
        </div>
        <span style={S.badge}>{merchants.length} merchants</span>
      </div>

      <div style={S.tabs}>
        {[["all", "All"], ["wallet", "Has Wallet"], ["none", "No Wallet"]].map(([key, label]) => (
          <button key={key} style={{ ...S.tab, ...(filters.wallet === key ? S.tabActive : {}) }} onClick={() => setFilters((f) => ({ ...f, wallet: key }))}>
            {label} ({counts[key] ?? 0})
          </button>
        ))}
      </div>

      <MerchantFilterBar
        merchants={merchants}
        filters={filters}
        onChange={setFilters}
        resultCount={filtered.length}
      />

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Store / Email", "Plan", "Sub Expires", "Referred By", "Wallet", "Balance", "Last Login", "Joined", ""].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr><td colSpan={9} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No merchants found</td></tr>
            ) : paginated.map((m) => (
              <tr key={m._id} style={S.tr}>
                <td style={S.td}>
                  <div style={{ fontWeight: 600, color: BG, fontSize: 13 }}>{m.storeInfo?.storeName || "—"}</div>
                  <div style={{ fontSize: 11, color: "#999" }}>{m.email}</div>
                </td>
                <td style={S.td}>
                  <span style={{ background: isActive(m) ? "rgba(167,204,72,0.12)" : "#f5f5f5", color: isActive(m) ? "#5a8a00" : "#aaa", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                    {m.plan || "Free"}
                  </span>
                </td>
                <td style={{ ...S.td, fontSize: 12, whiteSpace: "nowrap" }}>
                  {m.subscriptionEnd ? (
                    <span style={{ color: Date.now() < m.subscriptionEnd ? "#5a8a00" : "#e74c3c" }}>
                      {new Date(m.subscriptionEnd).toLocaleDateString()}
                    </span>
                  ) : "—"}
                </td>
                <td style={{ ...S.td, fontSize: 12, color: "#888" }}>{m.refBy || "—"}</td>
                <td style={S.td}>
                  {m.wallet ? (
                    <span style={S.walletTag}><i className="bi bi-check-circle-fill" /> Yes</span>
                  ) : (
                    <span style={S.noWalletTag}><i className="bi bi-x-circle" /> No</span>
                  )}
                </td>
                <td style={{ ...S.td, fontWeight: 700, color: m.wallet ? BG : "#ccc" }}>
                  {m.wallet ? "₦" + (m.wallet.balance || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                </td>
                <td style={{ ...S.td, fontSize: 12, color: "#888" }}>{m.lastLogin ? new Date(m.lastLogin).toLocaleString() : "Never"}</td>
                <td style={{ ...S.td, fontSize: 12, color: "#888" }}>{m.createdAt ? new Date(m.createdAt).toLocaleDateString() : "—"}</td>
                <td style={S.td}>
                  <button style={S.viewBtn} onClick={() => setSelected(m)}>
                    <i className="bi bi-eye" /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={S.pagination}>
          <span style={S.pageInfo}>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={S.pageControls}>
            <button style={{ ...S.pageBtn, ...(page === 1 ? S.pageBtnDisabled : {}) }} onClick={() => goTo(page - 1)} disabled={page === 1}>
              <i className="bi bi-chevron-left" />
            </button>
            {pageNumbers().map((p, i) =>
              p === "..." ? (
                <span key={`ellipsis-${i}`} style={S.ellipsis}>…</span>
              ) : (
                <button
                  key={p}
                  style={{ ...S.pageBtn, ...(p === page ? S.pageBtnActive : {}) }}
                  onClick={() => goTo(p)}
                >
                  {p}
                </button>
              )
            )}
            <button style={{ ...S.pageBtn, ...(page === totalPages ? S.pageBtnDisabled : {}) }} onClick={() => goTo(page + 1)} disabled={page === totalPages}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
      )}

      {selected && (
        <MerchantDetailPanel
          merchant={selected}
          onClose={() => setSelected(null)}
          canViewSensitive={true}
        />
      )}
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  sub: { fontSize: 13, color: "#888", marginTop: 4 },
  badge: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", border: "1px solid " + PRIMARY, borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700 },
  tabs: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 },
  tab: { padding: "7px 14px", borderRadius: 20, border: "1.5px solid #e0e0e0", background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#888" },
  tabActive: { background: PRIMARY, borderColor: PRIMARY, color: "#fff" },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 700 },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  walletTag: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" },
  noWalletTag: { background: "#f5f5f5", color: "#bbb", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" },
  viewBtn: { background: BG, color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 },
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, flexWrap: "wrap", gap: 8 },
  pageInfo: { fontSize: 12, color: "#888" },
  pageControls: { display: "flex", alignItems: "center", gap: 4 },
  pageBtn: { minWidth: 32, height: 32, padding: "0 8px", border: "1.5px solid #e0e0e0", borderRadius: 6, background: "#fff", fontSize: 12, fontWeight: 600, color: "#555", cursor: "pointer" },
  pageBtnActive: { background: BG, borderColor: BG, color: "#fff" },
  pageBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  ellipsis: { padding: "0 4px", color: "#bbb", fontSize: 13 },
};
